import React, { FormEventHandler, Fragment } from 'react';
import Radio from '@mos-cat/radio';
import { Heading } from '@mos-cat/typography';
import Checkbox from '@mos-cat/checkbox';
import { select, t, Trans } from '@lingui/macro';
import { createStructuredSelector } from 'reselect';
import { connect } from 'dva';
import { Dispatch } from 'redux';
import { SPOUSE_FIELD_NAME } from '../../../constants/fieldNames';
import { getOnChange } from '../../../helpers/handlers';
import {
  getApplicantLivesWithSecondParent,
  getApplicantSexValue,
  isApplicantMarried,
} from '../../../selectors/applicant';
import {
  childHasBirthdate,
  isChild18YearsOld,
  showAddressHints,
  showSpouseAddressOption,
  isChildRegisteredInMoscow,
} from '../../../selectors/child';
import { CHILD } from '../../../models/child';
import { CHILD_ADDRESS } from '../../../constants/child';
import AddressDirectory from '../../shared/Address/AddressDirectory/AddressDirectory';
import { Field } from '../../../declarations/widget-ums';
import { Sex } from '../../shared/PersonGender/PersonGender';
import { i18n } from '../../../helpers/i18n';

interface Props {
  addressMatchWithApplicant: Field<CHILD_ADDRESS | null>;
  hasBirthdate: boolean;
  is18YearsOld: boolean;
  isApplicantMarried: boolean;
  doesApplicantLivesWithSecondParent: boolean;
  applicantSex: Sex;
  onChange: FormEventHandler;
  isSpouseAddressCompleted: boolean;
  addressHints: { applicantAddress: string; spouseAddress: string };
  showSpouseAddressOption: boolean;
  isRegisteredInMoscow: boolean;
  toggleRegisteredInMoscow: FormEventHandler;
}

function ChildAddress({
  addressMatchWithApplicant,
  hasBirthdate,
  is18YearsOld,
  isApplicantMarried,
  doesApplicantLivesWithSecondParent,
  applicantSex,
  onChange,
  addressHints,
  showSpouseAddressOption,
  isRegisteredInMoscow,
  toggleRegisteredInMoscow,
}: Props): JSX.Element | null {
  const { value, error } = addressMatchWithApplicant;
  const name = `child/${SPOUSE_FIELD_NAME.addressMatchWithApplicant}`;
  const childAddressMatchesNoneOfTheParents =
    value === CHILD_ADDRESS.notMatchWithSpouse;
  const livesWithSecondParentOrMarried =
    isApplicantMarried || doesApplicantLivesWithSecondParent;

  /**
   * Макрос не поддерживает {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Object_initializer#Computed_property_names|computedProperties}, потому мы оперируем литералами `m` и `f`
   */
  const secondParentRadioLabelMatch = i18n._(
    select({
      value: String(isApplicantMarried),
      other: 'Совпадает с адресом второго родителя',
      true: select({
        value: applicantSex,
        f: 'Совпадает с адресом супруга заявителя',
        m: 'Совпадает с адресом супруги заявителя',
        other: 'Совпадает с адресом супруга (супруги) заявителя',
      }),
    })
  );

  const secondParentRadioLabelNotMatch = i18n._(
    select({
      value: String(livesWithSecondParentOrMarried && showSpouseAddressOption),
      other: 'Не совпадает с адресом заявителя',
      true: select({
        value: String(isApplicantMarried),
        true: select({
          value: applicantSex,
          other:
            'Не совпадает с адресами заявителя и супруга (супруги) заявителя',
          f: 'Не совпадает с адресами заявителя и супруга заявителя',
          m: 'Не совпадает с адресами заявителя и супруги заявителя',
        }),
        other: 'Не совпадает с адресами заявителя и второго родителя',
      }),
    })
  );

  if (hasBirthdate && !is18YearsOld) {
    return (
      <Fragment>
        <div className="StepContentBlock ChildAddress">
          <Heading
            level={3}
            className="PguFormGrid__row ChildAddressRadio__Title"
          >
            <Trans>Адрес регистрации по месту жительства</Trans>
          </Heading>
          <div className="PguFormGrid__row">
            <Radio
              className="PguFormGrid_col"
              label={i18n._(t`Совпадает с адресом заявителя`)}
              name={name}
              value={CHILD_ADDRESS.matchWithApplicant}
              checked={value === CHILD_ADDRESS.matchWithApplicant}
              onChange={onChange}
              error={error}
            />
          </div>
          <div className="PguFormGrid__row ChildAddress_address-hint">
            <p className="PguFormGrid_col ChildAddress_address-text ym-hide-content">
              {addressHints.applicantAddress}
            </p>
          </div>
          {showSpouseAddressOption && (
            <Fragment>
              <div className="PguFormGrid__row">
                <Radio
                  className="PguFormGrid_col"
                  label={secondParentRadioLabelMatch}
                  name={name}
                  value={CHILD_ADDRESS.matchWithSpouse}
                  checked={value === CHILD_ADDRESS.matchWithSpouse}
                  onChange={onChange}
                  error={error}
                />
              </div>
              <div className="PguFormGrid__row ChildAddress_address-hint">
                <p className="PguFormGrid_col ChildAddress_address-text ym-hide-content">
                  {addressHints['spouseAddress']}
                </p>
              </div>
            </Fragment>
          )}
          <div className="PguFormGrid__row">
            <Radio
              className="PguFormGrid_col"
              label={secondParentRadioLabelNotMatch}
              name={name}
              value={CHILD_ADDRESS.notMatchWithSpouse}
              checked={value === CHILD_ADDRESS.notMatchWithSpouse}
              onChange={onChange}
              error={error}
            />
          </div>
          {childAddressMatchesNoneOfTheParents && (
            <div className="ChildAddress__manual">
              <Checkbox
                className="RegisteredInMoscowCheckbox"
                checked={isRegisteredInMoscow}
                value="isRegisteredInMoscow"
                onChange={toggleRegisteredInMoscow}
                label={i18n._(t`Местом жительства является Москва`)}
              />
              {isRegisteredInMoscow && (
                <Fragment>
                  <div className="Content__sub">
                    <div className="Content__subText">
                      <Trans>
                        Для ввода адреса в Зеленограде, в случае отсутствия
                        улицы и дома, укажите адрес в формате: «Зеленоград,
                        корпус N»
                      </Trans>
                    </div>
                  </div>
                  <AddressDirectory model={CHILD} />
                </Fragment>
              )}
            </div>
          )}
        </div>
      </Fragment>
    );
  }
  return null;
}

const mapStateToProps = createStructuredSelector<any, any>({
  hasBirthdate: childHasBirthdate,
  is18YearsOld: isChild18YearsOld,
  addressMatchWithApplicant: (state) => state.child.addressMatchWithApplicant,
  applicantSex: getApplicantSexValue,
  isApplicantMarried: isApplicantMarried,
  doesApplicantLivesWithSecondParent: getApplicantLivesWithSecondParent,
  addressHints: showAddressHints,
  showSpouseAddressOption,
  isRegisteredInMoscow: isChildRegisteredInMoscow,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onChange: getOnChange(CHILD, dispatch),
  toggleRegisteredInMoscow({ target: { checked } }) {
    dispatch({
      type: 'child/address/toggle/isRegisteredInMoscow',
      payload: checked,
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ChildAddress);
